import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { X, ImageUp, Eye, EyeOff, CheckCircle2, Building2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  StaffGender,
  StaffGenderLabels,
  StaffDepartment,
  StaffDepartmentLabels,
} from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";
import { ClinicService } from "../../services/api/clinic.service";
import { ExitConfirmationModal } from "../ExitConfirmationModal";

interface StaffOnboardingModalProps {
  isOpen: boolean;
  clinicId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const StaffOnboardingModal: React.FC<StaffOnboardingModalProps> = ({
  isOpen,
  clinicId,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    mobileNumber: "",
    password: "",
    gender: StaffGender.MALE as string,
    department: StaffDepartment.PHARMACY as string,
    designation: "",
  });

  const [selectedClinicId, setSelectedClinicId] = useState<string>(clinicId || "");
  const [clinicsList, setClinicsList] = useState<{ id: string; name: string; city?: string }[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (clinicId) {
        setSelectedClinicId(clinicId);
      } else {
        fetchClinics();
      }
    }
  }, [isOpen, clinicId]);

  const fetchClinics = async () => {
    try {
      setIsLoadingClinics(true);
      const res = await ClinicService.getClinics({ page: 1, pageSize: 100, searchText: "" });
      if (res.success && res.data?.dataList) {
        setClinicsList(
          res.data.dataList.map((c: any) => ({
            id: c.id || c.clinicId,
            name: c.name || c.clinicName,
            city: c.city,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching clinics for staff onboarding:", err);
    } finally {
      setIsLoadingClinics(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = (file: File | undefined) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Photo size exceeds 5MB limit");
      return;
    }
    setProfilePhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handlePhotoSelect(e.dataTransfer.files[0]);
    }
  };

  const isFormDirty = () => {
    return Boolean(
      formData.firstName ||
      formData.lastName ||
      formData.username ||
      formData.mobileNumber ||
      formData.password ||
      formData.designation ||
      profilePhotoFile ||
      (!clinicId && selectedClinicId)
    );
  };

  const handleRequestClose = () => {
    if (isFormDirty()) {
      setShowExitConfirm(true);
    } else {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If no clinic selected, fall back to admin's user ID
    const targetClinicId = selectedClinicId || clinicId || user?.id || "";
    if (!targetClinicId) {
      toast.error("Please select a clinic or ensure admin ID is available");
      return;
    }

    if (!formData.firstName.trim()) {
      toast.error("Please enter first name");
      return;
    }
    if (!formData.username.trim()) {
      toast.error("Please enter username");
      return;
    }
    if (!formData.mobileNumber.trim()) {
      toast.error("Please enter mobile number");
      return;
    }
    if (!formData.password) {
      toast.error("Please enter password");
      return;
    }

    try {
      setIsSubmitting(true);

      const fullName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(" ");

      const payload = new FormData();
      payload.append("role", "Staff");
      payload.append("clinicId", targetClinicId);
      payload.append("name", fullName);
      payload.append("username", formData.username.trim());
      payload.append("mobileNumber", formData.mobileNumber.trim());
      payload.append("password", formData.password);
      payload.append("gender", formData.gender);
      payload.append("department", formData.department);
      payload.append("designation", formData.designation.trim());

      if (profilePhotoFile) {
        payload.append("profilePhoto", profilePhotoFile);
      }

      const res = await ClinicStaffService.signupStaff(payload);
      if (res.success) {
        toast.success(res.message || "Staff member added successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to add staff member");
      }
    } catch (error) {
      console.error("Add staff error:", error);
      toast.error("Error creating staff account");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-150">
          {/* Header matching modal reference */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
            <h2 className="text-base font-bold text-emerald-700 tracking-wide uppercase">
              ADD NEW STAFF
            </h2>
            <button
              onClick={handleRequestClose}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            {/* Photo Upload Box */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-xl p-5 text-center cursor-pointer transition-all border ${
                isDragging
                  ? "border-emerald-500 bg-emerald-50/50"
                  : photoPreview
                  ? "border-emerald-200 bg-emerald-50/20"
                  : "border-gray-100 bg-gray-50/80 hover:bg-gray-50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
                className="hidden"
              />

              {photoPreview ? (
                <div className="flex flex-col items-center justify-center gap-1.5">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-emerald-500 shadow-sm">
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs font-semibold text-emerald-700">Photo selected</p>
                  <p className="text-[11px] text-gray-400">Click or drag to change</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <ImageUp className="w-6 h-6" />
                  </div>
                  <p className="text-xs font-medium text-gray-700">
                    Click to upload or drag and drop your photo
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Upload photo (optional)
                  </p>
                </div>
              )}
            </div>

            {/* Clinic Selector (when opened by Admin or when clinicId is not fixed) */}
            {!clinicId && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Select Clinic
                </label>
                <div className="relative">
                  <select
                    value={selectedClinicId}
                    onChange={(e) => setSelectedClinicId(e.target.value)}
                    disabled={isLoadingClinics}
                    className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 cursor-pointer disabled:bg-gray-50"
                  >
                    <option value="">
                      {isLoadingClinics ? "Loading clinics..." : "Choose Clinic"}
                    </option>
                    {user?.id && (
                      <option value={user.id}>
                        Admin {user.name ? `(${user.name})` : ""}
                      </option>
                    )}
                    {clinicsList.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.city ? `(${c.city})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* First Name & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  placeholder="e.g. Ayush"
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  placeholder="e.g. Sahu"
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Username & Mobile Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange("username", e.target.value)}
                  placeholder="Enter username"
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(e) => handleChange("mobileNumber", e.target.value)}
                  placeholder="Enter mobile number"
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Password & Gender */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-3.5 py-2 pr-10 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Gender
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 cursor-pointer"
                  required
                >
                  {Object.values(StaffGender).map((g) => (
                    <option key={g} value={g}>
                      {StaffGenderLabels[g]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Department & Designation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Department
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleChange("department", e.target.value)}
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 cursor-pointer"
                  required
                >
                  {Object.values(StaffDepartment).map((d) => (
                    <option key={d} value={d}>
                      {StaffDepartmentLabels[d]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Designation
                </label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => handleChange("designation", e.target.value)}
                  placeholder="Enter designation (e.g. Pharmacist)"
                  className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Action Buttons matching modal design */}
            <div className="flex items-center gap-3 pt-3">
              <button
                type="button"
                onClick={handleRequestClose}
                disabled={isSubmitting}
                className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Adding Staff...
                  </>
                ) : (
                  "Add Staff"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ExitConfirmationModal
        isOpen={showExitConfirm}
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={() => {
          setShowExitConfirm(false);
          onClose();
        }}
      />
    </>
  );
};
